/**
 * V7-A (A3) — Regime-Leser für die „große Sicht".
 *
 * Liest die Treiber-ETF-Kurse aus v5_ohlcv_history, bestimmt je Treiber den
 * Trend (Kurs über/unter MA50 + 13-Wochen-Bewegung → up/down/flat) und reicht
 * das an die fertige, getestete Lib `intermarket.ts`. Konservativ: nur klare
 * Trends werden up/down, sonst flat (keine Glaskugel).
 *
 * Anleihen: TLT (Kurs) ist INVERS zu den Zinsen → für den 'rates'-Treiber
 * invertiert (TLT↑ = Zinsen↓).
 */
import { supabase } from '@/lib/supabase';
import {
  computeIntermarketRegime,
  type IntermarketInput,
  type IntermarketRegime,
  type Trend,
} from '@/lib/intermarket';

const DRIVER_SYMBOLS = ['UUP', 'CPER', 'USO', 'GLD', 'SLV', 'TLT', 'HYG', 'SPY'];

function trendFromCloses(closes: number[]): { trend: Trend; change13w: number } {
  const n = closes.length;
  if (n < 60) return { trend: 'flat', change13w: 0 };
  const close = closes[n - 1]!;
  const ma = (p: number) => {
    const s = closes.slice(-Math.min(p, n));
    return s.reduce((a, b) => a + b, 0) / s.length;
  };
  const ma50 = ma(50);
  const idx13 = Math.max(0, n - 1 - 65); // ~13 Wochen ≈ 65 Handelstage
  const ret13w = (close / closes[idx13]! - 1) * 100;
  let trend: Trend;
  if (close > ma50 && ret13w > 2) trend = 'up';
  else if (close < ma50 && ret13w < -2) trend = 'down';
  else trend = 'flat';
  return { trend, change13w: Math.round(ret13w * 10) / 10 };
}

function invert(t: Trend): Trend {
  return t === 'up' ? 'down' : t === 'down' ? 'up' : 'flat';
}

export interface IntermarketContext {
  regime: IntermarketRegime;
  input: IntermarketInput;
}

export async function fetchIntermarketContext(): Promise<IntermarketContext> {
  const cutoff = new Date(Date.now() - 320 * 86_400_000).toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from('v5_ohlcv_history')
    .select('symbol, close')
    .eq('timeframe', 'daily')
    .in('symbol', DRIVER_SYMBOLS)
    .gte('bar_date', cutoff)
    .order('bar_date', { ascending: true });
  if (error) throw new Error(`v5_ohlcv_history: ${error.message}`);

  const bySymbol = new Map<string, number[]>();
  for (const r of (data ?? []) as Array<{ symbol: string; close: number }>) {
    const arr = bySymbol.get(r.symbol) ?? [];
    arr.push(Number(r.close));
    bySymbol.set(r.symbol, arr);
  }
  const t = (sym: string) => {
    const c = bySymbol.get(sym);
    return c && c.length > 0 ? trendFromCloses(c) : null;
  };

  const tlt = t('TLT');
  const input: IntermarketInput = {
    dollar: t('UUP') ?? undefined,
    copper: t('CPER') ?? undefined,
    oil: t('USO') ?? undefined,
    gold: t('GLD') ?? undefined,
    silver: t('SLV') ?? undefined,
    highyield: t('HYG') ?? undefined,
    stocks: t('SPY') ?? undefined,
    // Zinsen invers zu den Anleihen-Kursen (TLT)
    rates: tlt ? { trend: invert(tlt.trend), change13w: -tlt.change13w } : undefined,
  };

  return { regime: computeIntermarketRegime(input), input };
}
